<template>
  <div class="bg-neutral-50 dark:bg-zinc-900 min-h-screen w-full pb-20">
    
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
        
        <div class="flex flex-col justify-center space-y-6 order-2 lg:order-1">
          <div class="space-y-2">
            <span class="text-amber-600 font-bold tracking-widest uppercase text-sm">Service Details</span>
            <h1 class="text-4xl md:text-5xl font-extrabold text-zinc-900 dark:text-white leading-tight">
              {{ selectedProduct?.name || 'Untitled Service' }}
            </h1>
          </div>
          
          <div class="w-20 h-1 bg-amber-500"></div> <p class="text-lg text-zinc-600 dark:text-zinc-300 leading-relaxed">
            {{ selectedProduct?.description || 'No description available for this service.' }}
          </p>
        </div>

        <div class="order-1 lg:order-2 relative aspect-[4/3] rounded-xl overflow-hidden shadow-2xl group">
          <div v-if="!isMainImageLoaded" class="absolute inset-0 bg-zinc-200 dark:bg-zinc-800 animate-pulse"></div>
          
          <img 
            v-if="selectedProduct?.main_image"
            v-show="isMainImageLoaded" 
            :src="selectedProduct.main_image" 
            alt="Main Service View" 
            class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
            @load="onMainImageLoad" 
          />
           <div v-else class="flex items-center justify-center h-full bg-zinc-200 text-zinc-400">No Image</div>
        </div>
      </div>

      <div v-if="selectedProduct?.images && selectedProduct.images.length > 0" class="space-y-8">
        <h2 class="text-2xl font-bold text-zinc-800 dark:text-white border-l-4 border-amber-500 pl-4">
          Service Gallery
        </h2>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            v-for="(image, index) in selectedProduct.images" 
            :key="index" 
            class="relative group aspect-[4/3] overflow-hidden rounded-lg shadow-md cursor-pointer bg-zinc-200 dark:bg-zinc-800"
            @click="openImage(image)"
          >
            <div v-if="!isImageLoaded[image]" class="absolute inset-0 bg-zinc-300 dark:bg-zinc-700 animate-pulse z-10"></div>
            
            <img 
              v-show="isImageLoaded[image]" 
              :src="image" 
              alt="Service Gallery Image" 
              class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
              @load="onImageLoad(image)" 
            />

            <div class="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
              <span class="opacity-0 group-hover:opacity-100 text-white text-3xl drop-shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                &plus;
              </span>
            </div>
          </div>
        </div>
      </div>

    </div>

    <Transition name="fade">
      <div 
        v-if="fullscreenImage" 
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-zinc-900/95 backdrop-blur-sm p-4" 
        @click.self="closeImage"
      >
        <button 
          class="absolute top-6 right-6 text-white/70 hover:text-white transition-colors focus:outline-none" 
          @click="closeImage"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-10 h-10">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <img 
          :src="fullscreenImage" 
          class="max-w-full max-h-[90vh] rounded-md shadow-2xl object-contain select-none" 
          alt="Fullscreen View" 
        />
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
  selectedProduct: {
    type: Object,
    default: () => null,
  }
});

const fullscreenImage = ref(null);
const isMainImageLoaded = ref(false);
const isImageLoaded = ref({});

function openImage(image) {
  fullscreenImage.value = image;
  // Prevent body scroll when modal is open
  document.body.style.overflow = 'hidden';
}

function closeImage() {
  fullscreenImage.value = null;
  // Restore body scroll
  document.body.style.overflow = '';
}

function onMainImageLoad() {
  isMainImageLoaded.value = true;
}

function onImageLoad(image) {
  isImageLoaded.value[image] = true;
}
</script>

<style scoped>
/* Vue Transition for Modal Fade */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>